import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import { selectIsLoading } from '../../model/slice';
import type { DatasourceType } from '../../model/types';
import { DatasourceStatus } from '../../model/types';
import { DatasourceIds, useDatasources } from '../../model/useDatasources';
import { DatasourceCard } from '../DatasourceCard/DatasourceCard';

const STATUS_MAP: Record<DatasourceStatus, number> = {
  [DatasourceStatus.error]: 0,
  [DatasourceStatus.active]: 1,
  [DatasourceStatus.inactive]: 2,
};

interface Props {
  datasourceTypes: DatasourceType[];
  searchQuery: string;
}

export function Marketplace({ datasourceTypes, searchQuery }: Props) {
  const integrationsLoading = useAppSelector(selectIsLoading);
  const datasources = useDatasources();
  const filteredDatasources = useMemo(() => {
    const typesSet = new Set(datasourceTypes);
    const preparedQuery = searchQuery.toLowerCase();

    return datasources
      .filter((datasource) => {
        return (
          datasource.name.toLocaleLowerCase().includes(preparedQuery) &&
          (typesSet.size === 0 ||
            datasource.id === DatasourceIds.custom ||
            typesSet.has(datasource.type))
        );
      })
      .sort((a, b) => {
        if (a.status !== b.status) {
          const aStatus = STATUS_MAP[a.status];
          const bStatus = STATUS_MAP[b.status];

          return aStatus - bStatus;
        }

        if (a.id === DatasourceIds.custom) {
          return 1;
        }

        if (b.id === DatasourceIds.custom) {
          return -1;
        }

        return a.name.localeCompare(b.name);
      });
  }, [searchQuery, datasourceTypes, datasources]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyleType: 'none',

          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'auto',

          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(244px, 1fr))',
          gridTemplateRows: 'repeat(auto-fit, 170px)',
          gap: 3,
        }}
      >
        {filteredDatasources.length === 0 && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 4,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#7E828F',
                textAlign: 'center',
              }}
            >
              No integrations match your search criteria.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#7E828F',
                textAlign: 'center',
                mb: 1,
              }}
            >
              Can't find the integration you need?{' '}
              <Link
                style={{
                  color: 'var(--accent-700)',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
                to="./custom"
              >
                Create a custom integration
              </Link>
            </Typography>
          </Box>
        )}
        {filteredDatasources.map((datasource) => {
          return (
            <li key={datasource.id}>
              <Link
                style={{ textDecoration: 'none' }}
                to={`./${datasource.id}`}
              >
                <DatasourceCard datasource={datasource} />
              </Link>
            </li>
          );
        })}
      </Box>
      <Loader active={integrationsLoading} />
    </Box>
  );
}
